/**
 * Berserker screen (V3b — SM-7, route /berserker; PRD §5.9 #88, D-003/D-010),
 * v3 "AFTER-DARK ARCADE": theatrical invincibility.
 *
 * ACTIVE: the fire creeps into the UI — a fixed ember atmosphere breathes at
 * the screen edges, the card frame pulses, the Anton countdown burns in the
 * berserk gradient with a faint (bounded, slow — seizure-safe) glitch tic,
 * and the juggernaut badge rides the HUD. All of it is THEATER, and the
 * screen says so out loud: the ©berserk.store_honesty line renders as a calm,
 * unthemed compliance panel — block & report still work on you, in full,
 * instantly (D-003). The fire never touches it.
 *
 * INACTIVE: the activate pitch — the consent-critical confirm sheet stays
 * dead serious (8 h, never pauses, no refunds — EC-24) + the same honesty
 * line; balance 0 routes to the store (©store.berserker_pitch).
 *
 * Two states, all server-derived from /me/inventory (the client renders,
 * never computes — PRD §7.4). Announcer stings ride the pushes (motif "berserk").
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatCopy, resolveCopy } from "@humble/shared";
import { announce, BerserkerBadge, Button, Card, Sheet, toast } from "../../design/index.js";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { useCountdown } from "../cooldown/use-countdown.js";
import { useFeatureToggles } from "../settings/use-feature-toggles.js";
import "./berserker.css";

interface SessionView {
  active?: boolean;
  activated_at?: string;
  expires_at?: string;
}

export function BerserkerScreen() {
  const [session, setSession] = useState<SessionView>({ active: false });
  const [balance, setBalance] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  // S-27 game toggles: activation affordance hides when the user opted out
  // (server refuses regardless — BERSERKER_DISABLED). A session already
  // running keeps its HUD; the toggle gates NEW activations only.
  const berserkerEnabled = useFeatureToggles().berserker_enabled;

  const refresh = useCallback(async () => {
    try {
      const inv = await api.get("/me/inventory");
      setSession((inv.berserker as SessionView | undefined) ?? { active: false });
      setBalance((inv.balances as { berserker?: number } | undefined)?.berserker ?? 0);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, []);

  useEffect(() => {
    void refresh();
    ws.ensureConnected();
    const offs = [
      ws.on("berserker.started", () => {
        announce({ sting: resolveCopy("announcer.berserk_start") ?? "BERSERKER MODE.", motif: "berserk" });
        void refresh();
      }),
      ws.on("berserker.ended", () => {
        announce({ sting: resolveCopy("announcer.berserk_end") ?? "Berserker Mode has ended.", motif: "berserk" });
        void refresh();
      }),
      ws.on("inventory.updated", () => void refresh()),
    ];
    return () => offs.forEach((off) => off());
  }, [refresh]);

  const active = Boolean(session.active);
  const remaining = useCountdown(active ? (session.expires_at ?? null) : null);

  // The 8 h wind-down: when the client-side countdown floors, re-ask the server.
  useEffect(() => {
    if (active && remaining === "00:00:00") {
      const t = setTimeout(() => void refresh(), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, remaining, refresh]);

  async function activate() {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/arsenal/berserker/activate", { body: withEventId({}) });
      setConfirming(false);
      await refresh();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`berserk${active ? " berserk--active" : ""}`}>
      {active && (
        /* the fire creeps in — ember pools + pulsing edges, pure decoration */
        <div className="berserk-atmosphere" aria-hidden="true">
          <div className="berserk-atmosphere__embers" />
          <div className="berserk-atmosphere__edges" />
        </div>
      )}

      <Card
        padding="lg"
        glow={active ? "berserk" : "none"}
        className={`berserk__card${active ? " crt" : ""}`}
      >
        <div className="berserk__head">
          <h1 className="type-display-3 berserk__title">Berserker Mode</h1>
          {active && <BerserkerBadge />}
        </div>

        {active ? (
          <div className="berserk__hud">
            <div
              className="berserk__timer type-numeric"
              role="timer"
              aria-label="Berserker time remaining"
            >
              {formatCopy(resolveCopy("berserk.hud_active") ?? "BERSERKER · {hh:mm}", {
                "hh:mm": remaining,
              })}
            </div>
            <p className="text-dim">{resolveCopy("berserk.perks")}</p>
            {/* the honesty line — calm, unthemed, always legible (D-003):
                the invincibility is theater; safety is not */}
            <p className="berserk__honesty">{resolveCopy("berserk.store_honesty")}</p>
            <p className="text-faint">{resolveCopy("berserk.expired_note")}</p>
          </div>
        ) : (
          <>
            <p className="text-dim">{resolveCopy("berserk.perks")}</p>
            <p className="berserk__honesty">{resolveCopy("berserk.store_honesty")}</p>
            <div className="berserk__stock">
              <span className="badge">{balance ?? "–"} in stock</span>
            </div>
            {!berserkerEnabled ? (
              <>
                <p className="berserk__warning">
                  {resolveCopy("features.berserker_off_note") ??
                    "Berserker Mode is switched off in your settings."}
                </p>
                <Link to="/settings/features" className="btn btn--block">
                  {resolveCopy("features.settings_cta") ?? "Game features settings"}
                </Link>
              </>
            ) : (balance ?? 0) > 0 ? (
              <Button variant="berserk" size="lg" block onClick={() => setConfirming(true)}>
                GO BERSERK — 8 hours
              </Button>
            ) : (
              <>
                <p className="berserk__warning">{resolveCopy("berserk.no_credit")}</p>
                <Link to="/store" className="btn btn--primary btn--block">
                  {resolveCopy("store.berserker_pitch")}
                </Link>
              </>
            )}
          </>
        )}
      </Card>

      {/* consent-critical confirm — dead serious, no fire in here (EC-24) */}
      <Sheet
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Activate Berserker Mode?"
        dismissable={false}
      >
        <div className="berserk__confirm">
          <p>{resolveCopy("berserk.confirm")}</p>
          <p className="berserk__honesty">{resolveCopy("berserk.store_honesty")}</p>
          <div className="berserk__confirm-actions">
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              Not now
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void activate()}>
              Activate — 8h starts now
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
